import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CircleNotch, PaperPlaneTilt, Sparkle } from "@phosphor-icons/react";
import { api, getErrorMessage } from "@/lib/api";
import { useCustomer } from "@/context/CustomerContext";
import { inputCls, Label } from "./fields";

function useDraft(sessionId, caravan) {
  const [form, setForm] = useState({
    name: caravan?.customer_name || "", chassis_number: caravan?.chassis_number || "", phone: "", summary: "", note: "",
  });
  const [drafting, setDrafting] = useState(true);
  useEffect(() => {
    let alive = true;
    api.post("/support-tickets/draft", { session_id: sessionId }, { timeout: 30000 })
      .then(({ data }) => alive && setForm((f) => ({
        ...f,
        name: f.name || data.name, chassis_number: f.chassis_number || data.chassis_number, summary: f.summary || data.summary,
      })))
      .catch(() => {})
      .finally(() => alive && setDrafting(false));
    return () => { alive = false; };
  }, [sessionId]);
  return { form, setForm, drafting };
}

export function TicketForm({ messageId, onDone, onCancel }) {
  const { sessionId, caravan } = useCustomer();
  const { form, setForm, drafting } = useDraft(sessionId, caravan);
  const [sending, setSending] = useState(false);
  const phoneRef = useRef(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => { if (!drafting) phoneRef.current?.focus({ preventScroll: true }); }, [drafting]);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/support-tickets", { ...form, session_id: sessionId, message_id: messageId || null });
      toast.success(`Ticket ${data.reference} lodged`);
      onDone(data);
    } catch (err) { toast.error(getErrorMessage(err)); } finally { setSending(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-3.5" data-testid="ticket-form">
      <div className="grid sm:grid-cols-2 gap-3">
        <div><Label>Your name</Label><input data-testid="ticket-name-input" value={form.name} onChange={set("name")} className={inputCls} placeholder="First name" maxLength={80} /></div>
        <div><Label>Chassis number</Label><input data-testid="ticket-chassis-input" value={form.chassis_number} onChange={set("chassis_number")} className={`${inputCls} font-mono tracking-wider uppercase`} placeholder="OTM123456" maxLength={20} /></div>
      </div>
      <div><Label>Contact phone</Label><input ref={phoneRef} data-testid="ticket-phone-input" type="tel" inputMode="tel" autoComplete="tel" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="04xx xxx xxx" maxLength={30} /></div>
      <div>
        <Label hint={drafting ? null : "Written by Nick — edit anything"}>What's the issue?</Label>
        {drafting ? (
          <div className="flex items-center gap-2 text-sm text-neutral-500 bg-black/40 border border-neutral-800 rounded-xl px-3.5 py-3" data-testid="ticket-summary-drafting">
            <Sparkle size={15} className="text-[#73bf44] animate-pulse" weight="fill" /> Nick is summarising your chat…
          </div>
        ) : (
          <textarea data-testid="ticket-summary-input" value={form.summary} onChange={set("summary")} rows={4} className={`${inputCls} resize-y leading-relaxed`} placeholder="Describe the technical issue you need help with" maxLength={3000} />
        )}
      </div>
      <div><Label hint="Optional">Anything else?</Label><textarea data-testid="ticket-note-input" value={form.note} onChange={set("note")} rows={2} className={`${inputCls} resize-y`} placeholder="Best time to call, photos you can send, etc." maxLength={2000} /></div>
      <p className="text-[0.7rem] text-neutral-500 leading-relaxed">
        Technical support only. For a warranty claim, please use the <Link to="/warranty" className="text-[#73bf44] hover:underline" data-testid="ticket-warranty-link">Warranty Claim form</Link>.
      </p>
      <div className="flex items-center justify-end gap-2 pt-1">
        {onCancel && <button type="button" onClick={onCancel} data-testid="ticket-cancel-btn" className="text-sm text-neutral-400 hover:text-white px-4 py-2.5 rounded-full transition-colors">Cancel</button>}
        <button type="submit" disabled={sending || drafting} data-testid="ticket-submit-btn" className="inline-flex items-center gap-2 bg-[#73bf44] text-[#050505] rounded-full font-heading font-bold px-5 py-2.5 text-sm hover:bg-[#5fa338] transition-colors disabled:opacity-50">
          {sending ? <CircleNotch size={16} className="animate-spin" /> : <PaperPlaneTilt size={16} weight="fill" />} {sending ? "Sending…" : "Send ticket"}
        </button>
      </div>
    </form>
  );
}
